import { AsApp, GetMeshEvent } from "../as-app.ts";

// Define interfaces for queued operations
interface QueuedOperation {
  type: string;
  execute: (configurator: any) => Promise<void>;
}

interface QueuedUpload extends QueuedOperation {
  type: 'upload';
  file: File;
  fileParamName: string;
}

export class AsConfigurator extends HTMLElement {
  private configurator: any;
  
  private initialized: boolean = false;
  private waitingForSysInfo:Array<HTMLElement> = [];
  private waitingForConfigurator:Array<HTMLElement> = [];
  private undoCallbacks:Array<Function> = [];
  private app:AsApp;
  private operationQueue: QueuedOperation[] = [];
  private spinner: HTMLElement | null = null;
  public configuratorId:string;
  public apiKey:string;


  static get observedAttributes() {
    return ['configurator-id','api-key'];
  }

  constructor() {
    super();
    this.configurator = null;
    this.initialized = false;
    this.waitingForSysInfo = [];
    this.waitingForConfigurator = [];
    this.operationQueue = [];
    this.spinner = null;
  }
  async getParams(){
    // Wait for initialization if not ready
    if(!this.initialized){
      await new Promise<void>((resolve) => {
        const checkInit = () => {
          if(this.initialized){
            resolve();
          } else {
            setTimeout(checkInit, 100);
          }
        };
        checkInit();
      });
    }

    // Wait for any pending operations to complete
    if(this.operationQueue.length > 0){
      await this.processOperationQueue();
    }

    return this.configurator.params;
  }

  // Keep synchronous getter for backwards compatibility, but log warning
  get params(){
    if(!this.initialized){
      console.warn("AS-Configurator not initialized, params not available. Use getParams() async method instead.")
      return undefined
    }
    return this.configurator.params
  }
  connectedCallback(){
    window.addEventListener('as-app',(ev: CustomEvent<{sender: AsApp}>)=>{
      // console.log("CFGR: Got app", ev.detail.sender);
      this.app = ev.detail.sender;
    })
    window.addEventListener('configurator-initialized', this.handleConfiguratorInitialized);
    // window.addEventListener('set-configurator', this.setConfigurator);
    window.addEventListener('get-configurator', (ev: CustomEvent<{requester: HTMLElement}>)=>{
      if (!this.initialized) {  
        this.waitingForConfigurator.push(ev.detail.requester);
      } else {
        ev.detail.requester.dispatchEvent(new CustomEvent('configurator', {
          detail: {configurator:this.configurator}
        }));
      }
    });
    window.addEventListener('get-system-info', this.handleSystemInfoReq);
    // console.log("AS-CONFIGURATOR: Listeners created");

    window.addEventListener('get-configurator-mesh', async (ev:GetMeshEvent)=>{
      // console.log("AsConfigurator: Handling get-configurator-mesh event");
      try {
        if (!this.configurator) {
          // console.error("AsConfigurator: No configurator available");
          const error = new Error("Configurator not initialized");
          error.name = "ConfiguratorMissingError";
          Object.assign(error, {
            context: "Configurator instance was not available when attempting to get mesh",
            component: "as-configurator",
            initialized: this.initialized
          });
          ev.detail.reject(error);
          return;
        }
        
        // Check if we need a finalized model URL
        const finalized = ev.detail.finalized === true;
        const filename = ev.detail.filename || 'model.stl';
        const type = ev.detail.type || 'stl';
        
        // Get appropriate URL based on finalized flag
        const meshURL = finalized 
          ? this.configurator.getFinalizedModelUrl(type, filename)
          : this.configurator.getModelUrl();
        
        // console.log("AsConfigurator: Got mesh URL", meshURL ? "✓" : "✗");
        
        if (!meshURL) {
          // console.error("AsConfigurator: Mesh URL is empty or undefined");
          const error = new Error("Could not get mesh URL from configurator");
          error.name = "MeshUrlMissingError";
          Object.assign(error, {
            context: "The configurator did not return a valid mesh URL",
            component: "as-configurator",
            configuratorSession: this.configurator.configSession,
            finalized: finalized
          });
          ev.detail.reject(error);
          return;
        }
        
        // console.log("AsConfigurator: Fetching mesh from URL");
        const meshBlob = await fetch(meshURL).then(res => {
          if (!res.ok) {
            const error = new Error(`Failed to fetch mesh: ${res.status} ${res.statusText}`);
            error.name = "MeshFetchFailedError";
            Object.assign(error, {
              context: "HTTP request to get mesh data failed",
              component: "as-configurator",
              statusCode: res.status,
              statusText: res.statusText,
              url: meshURL
            });
            throw error;
          }
          return res.blob();
        });
        
        // console.log("AsConfigurator: Successfully fetched mesh blob");
        ev.detail.resolve(meshBlob);
      } catch (err) {
        // console.error("AsConfigurator: Error getting mesh:", err);
        // Ensure the error has a proper name and context for the error panel
        if (err instanceof Error && !err.name?.includes("Error")) {
          err.name = "MeshGenerationError";
          Object.assign(err, {
            context: "Error occurred during mesh generation or fetching",
            component: "as-configurator",
            configuratorSession: this.configurator?.configSession
          });
        }
        ev.detail.reject(err);
      }
    })
    window.addEventListener('upload-file', this.handleUploadFile);

    window.addEventListener('as-configure',async (ev)=>{
      console.log("Got configure req")
      this.configure({
        ...ev.detail.params
      })
    })

    // Handle direct mesh URL requests from export buttons
    window.addEventListener('get-mesh-url', async (ev: CustomEvent) => {
      try {
        if (!this.configurator) {
          ev.detail.reject(new Error("Configurator not initialized"));
          return;
        }

        const { type = 'stl', filename, finalized = true } = ev.detail;

        // Get appropriate URL based on finalized flag
        const meshURL = finalized
          ? this.configurator.getFinalizedModelUrl(type, filename)
          : this.configurator.getModelUrl();

        if (!meshURL) {
          ev.detail.reject(new Error("Could not get mesh URL from configurator"));
          return;
        }

        ev.detail.resolve(meshURL);
      } catch (err) {
        ev.detail.reject(err);
      }
    })
  }

  configure =(params)=> this.configurator?.configure(params)
  
  handleSystemInfoReq = (event) => {
    // console.log("AS-CONFIGURATOR: Getting System info for", event.detail.requester);
    if (!this.initialized) {
      // console.log("AS-CONFIGURATOR: Not initialized, adding to queue");
      this.waitingForSysInfo.push(event.detail.requester);
    } else {
      this.sendSystemInfo(event.detail.requester);
    }
  }

  // onPostUndo = (callback:Function) => {
  //   this.undoCallbacks.push(callback);
  // }

  // handleUndo = (event) => {
  //   // console.log("AS-CONFIGURATOR: Undoing", event.detail);
  //   this.configurator.undo();
  //   // window.dispatchEvent(new CustomEvent('post-undo', { detail: { configurator: this.configurator } }));
  // }


  getSystemInfo = () => {
    // this.configurator.getLog().then(log=>{
    //   console.log("AS-CONFIGURATOR: Log", log);
    // })
    const info = {
      // trcadVersion:,
      sessionId:this.configurator.configSession,

    }
    // this.configurator
    return info;
  }



  // setConfigurator = (event) => {
  //   // console.log("AS-CONFIGURATOR: Setting configurator", event.detail);
  //   this.configurator = event.detail.configurator;
  //   this.initialized = true;
  //   this.sendSystemInfoToQueue();
  // }
  
  sendSystemInfoToQueue = () => {
    // console.log("AS-CONFIGURATOR: Sending System info to queue");
    this.waitingForSysInfo.forEach(requester => {
      this.sendSystemInfo(requester);
    });
  }

  sendSystemInfo = (requester) => {
    const info = this.getSystemInfo();
    requester.dispatchEvent(new CustomEvent('system-info', { detail: { info } }));
  }
  
  // Queue an operation to be executed when configurator is ready
  queueOperation = (operation: QueuedOperation) => {
    // console.log(`AS-CONFIGURATOR: Queueing ${operation.type} operation`);
    
    // Add to queue
    this.operationQueue.push(operation);
    
    // If this is the first queued operation, show a spinner
    if (this.operationQueue.length === 1 && !this.spinner) {
      this.spinner = document.createElement('as-spinner');
      document.body.appendChild(this.spinner);
    }
  }
  
  // Process all queued operations
  processOperationQueue = async () => {
    if (this.operationQueue.length === 0) return;
    
    // console.log(`AS-CONFIGURATOR: Processing ${this.operationQueue.length} queued operations`);
    
    // Remove spinner if it exists
    if (this.spinner) {
      this.spinner.remove();
      this.spinner = null;
    }
    
    // Make a copy of the queue and clear it before processing
    // This prevents new operations added during processing from being lost
    const queueToProcess = [...this.operationQueue];
    this.operationQueue = [];
    
    // Process each queued operation
    for (const operation of queueToProcess) {
      try {
        // console.log(`AS-CONFIGURATOR: Processing queued ${operation.type} operation`);
        await operation.execute(this.configurator);
      } catch (err) {
        // console.error(`AS-CONFIGURATOR: Error processing ${operation.type} operation:`, err);
      }
    }
  }

  uploadFile = async(file,fileParamName) => new Promise( async(resolve,reject)=>{
    const uploadFn = async () => {
      try {
        const uploadRes = await this.configurator.uploadFile({
          file,
          fileId: fileParamName,
        });
        // console.log("AS-CONFIGURATOR: Queued file uploaded", uploadRes.token);
        const parmRes = await this.configurator.setParamValue(fileParamName, uploadRes.token);
        resolve(parmRes);
      } catch (err) {
        // console.error("AS-CONFIGURATOR: Error uploading queued file:", err);
        reject(err);
      }
    }

    // If configurator is not initialized, queue the upload operation
    if (!this.configurator || !this.initialized) {
      const uploadOperation: QueuedUpload = {
        type: 'upload',
        file,
        fileParamName,
        execute: uploadFn
      };
      
      this.queueOperation(uploadOperation);
      return;
    }
    else return uploadFn();
  })

  private handleUploadFile = (event:CustomEvent<{
    file:File,
    fileParamName:string,
    resolve:Function,
    reject:Function,
    progress?:Function
  }>)=>{
    const {file,fileParamName,resolve,reject,progress} = event.detail;
    // console.log("AS-CONFIGURATOR: Uploading file", fileParamName, file);
    
    // Check if file is a Blob without a name property (not a File)
    // If so, convert it to a File with a name and type
    
    
    // If configurator is not initialized, queue the upload operation
    if (!this.configurator || !this.initialized) {
      const uploadOperation: QueuedUpload = {
        type: 'upload',
        file,
        fileParamName,
        execute: async (configurator) => {
          try {
            const uploadRes = await configurator.uploadFile({
              file,
              fileId: fileParamName,
              progress,
            });
            // console.log("AS-CONFIGURATOR: Queued file uploaded", uploadRes.token);
            const parmRes = await configurator.setParamValue(fileParamName, uploadRes.token);
            resolve(uploadRes.token, parmRes);
          } catch (err) {
            // console.error("AS-CONFIGURATOR: Error uploading queued file:", err);
            reject(err);
          }
        }
      };
      
      this.queueOperation(uploadOperation);
      return;
    }
    
    // If configurator is ready, proceed with upload immediately
    this.configurator.uploadFile({
      file: file,
      fileId: fileParamName,
      progress,
      // success: (res)=>{console.log(res.token);}
    }).then((uploadRes:{token:string})=>{
      // console.log("AS-CONFIGURATOR: File uploaded", uploadRes.token);
      this.configurator.setParamValue(fileParamName,uploadRes.token).then((parmRes)=>{
        resolve(uploadRes.token,parmRes);
      })
    }).catch((err)=>{
      reject(err);
    });
  }
  
  handleConfiguratorInitialized = (event) => {
    // console.log("AS-CONFIGURATOR: Configurator initialized");
    this.initialized = true;
    this.configurator = event.detail.configurator;
    
    // Process any operations that were queued while waiting for initialization
    if (this.operationQueue.length > 0) {
      // console.log(`AS-CONFIGURATOR: Processing ${this.operationQueue.length} queued operations after initialization`);
      this.processOperationQueue();
    }
    
    this.sendSystemInfoToQueue();
    this.dispatchEvent(new CustomEvent('as-cfgr-comp-ready', { 
      bubbles: true,
      composed: true,
      detail: { sender:this } 
    }));
  }

  disconnectedCallback = () => {
    window.removeEventListener('configurator-initialized', this.handleConfiguratorInitialized);
    window.removeEventListener('get-system-info', this.handleSystemInfoReq);
    window.removeEventListener('upload-file', this.handleUploadFile);
    
    // Remove spinner if it exists
    if (this.spinner) {
      this.spinner.remove();
      this.spinner = null;
    }
  }
}
// console.log("Defining as-configurator")
if (!customElements.get('as-configurator')) { 
  customElements.define('as-configurator', AsConfigurator);
} else {
  console.warn("as-configurator already defined");
}


